import CustomButton from "@/components/common/Button";
import StateMasterBadge from "@/components/StateMasterBadge";
import { WorkflowRecordService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, MenuProps, Spin } from "antd";
import { ArrowRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

interface WorkflowStatusProps {
  schemaName: string;
  recordId: string;
}

const WorkflowStatus: React.FC<WorkflowStatusProps> = ({
  schemaName,
  recordId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  // Fetch workflow status
  const {
    data: currentWorkflowStatus,
    refetch: refetchWorkflowStatus,
    isLoading: isWorkflowStatusLoading,
  } = useQuery({
    queryKey: ["workflow-status", schemaName, recordId],
    queryFn: () =>
      WorkflowRecordService.getRecordWorkflowStatus(schemaName, recordId),
    enabled: !!schemaName && !!recordId,
  });

  // Handle workflow action
  const handleWorkflowAction = useCallback(
    async (actionId: string, actionName: string) => {
      setIsLoading(true);
      const response = await WorkflowRecordService.performWorkflowAction({
        schemaName,
        recordId,
        actionId,
        comment: `Action performed: ${actionName}`,
      });

      if (response) {
        refetchWorkflowStatus();
      }
      setIsLoading(false);
    },
    [schemaName, recordId, refetchWorkflowStatus]
  );

  // Create workflow menu items
  const workflowMenuItems: MenuProps["items"] = useMemo(() => {
    return (
      currentWorkflowStatus?.available_actions?.map(action => ({
        key: action.id,
        label: (
          <div className="flex w-full items-center justify-between">
            <span>{action.name}</span>
            <ArrowRight size={14} />
          </div>
        ),
        onClick: () => handleWorkflowAction(action.id, action.name),
      })) ?? []
    );
  }, [currentWorkflowStatus?.available_actions, handleWorkflowAction]);

  // Don't render if no record ID or schema name
  if (!recordId || !schemaName) {
    return null;
  }

  if (isWorkflowStatusLoading) {
    return <Spin />;
  }

  return (
    <div className={`flex items-center gap-2`}>
      {/* Current State Badge */}
      {currentWorkflowStatus?.current_state && (
        <StateMasterBadge
          stateMaster={{
            name: currentWorkflowStatus.current_state,
            background_color: currentWorkflowStatus.background_color,
            text_color: currentWorkflowStatus.text_color,
          }}
        />
      )}

      {/* Workflow Actions Dropdown */}
      {currentWorkflowStatus?.available_actions &&
        currentWorkflowStatus.available_actions.length > 0 && (
          <Dropdown
            menu={{ items: workflowMenuItems }}
            placement="bottomRight"
            trigger={["click"]}
            overlayStyle={{ minWidth: 200 }}
          >
            <CustomButton
              title="Workflow Actions"
              className="flex items-center gap-1"
              loading={isLoading}
            >
              Actions
            </CustomButton>
          </Dropdown>
        )}
    </div>
  );
};

export default WorkflowStatus;
